import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

export interface ManualReplyTarget {
  /** Reservation UUID (DB id). */
  reservationId: string;
  customerName: string;
  customerEmail: string;
  date: string; // YYYY-MM-DD or formatted
  time: string; // HH:mm
  numberOfPeople: number;
  notes?: string | null;
  establishmentName?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ManualReplyTarget | null;
  onSent?: () => void;
}

const SUBJECT_MAX = 200;
const BODY_MAX = 5000;

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function plainTextToHtml(text: string): string {
  return escapeHtml(text).replace(/\r?\n/g, "<br>");
}

function formatDateLabel(date: string): string {
  try {
    const d = new Date(date);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
  } catch {
    /* noop */
  }
  return date;
}

function buildDefaults(target: ManualReplyTarget) {
  const firstName = target.customerName?.trim().split(/\s+/)[0] || "";
  const dateLabel = formatDateLabel(target.date);
  const subject = `Re : votre réservation du ${dateLabel} à ${target.time}`;
  const lines: string[] = [];
  lines.push(`Bonjour ${firstName || ""}`.trim() + ",");
  lines.push("");
  lines.push(
    `Merci pour votre réservation pour ${target.numberOfPeople} personne${
      target.numberOfPeople > 1 ? "s" : ""
    } le ${dateLabel} à ${target.time}.`
  );
  if (target.notes && target.notes.trim().length > 0) {
    lines.push("");
    lines.push(`Au sujet de votre note : « ${target.notes.trim()} »`);
  }
  lines.push("");
  lines.push("");
  lines.push("Cordialement,");
  lines.push(
    `L'équipe ${target.establishmentName ? target.establishmentName : ""}`.trim()
  );
  return { subject, body: lines.join("\n") };
}

export function ManualReplyDialog({ open, onOpenChange, target, onSent }: Props) {
  const defaults = useMemo(
    () => (target ? buildDefaults(target) : { subject: "", body: "" }),
    [target]
  );
  const [subject, setSubject] = useState(defaults.subject);
  const [body, setBody] = useState(defaults.body);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && target) {
      setSubject(defaults.subject);
      setBody(defaults.body);
    }
  }, [open, target?.reservationId]);

  if (!target) return null;

  const canSend =
    !sending &&
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    subject.length <= SUBJECT_MAX &&
    body.length <= BODY_MAX;

  const handleSend = async () => {
    if (!canSend || !target) return;
    setSending(true);
    try {
      const customBodyHtml = `<div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #111827;">${plainTextToHtml(
        body
      )}</div>`;
      const { data, error } = await supabase.functions.invoke("send-manual-email", {
        body: {
          reservationId: target.reservationId,
          emailType: "manual",
          customSubject: subject.trim(),
          customBodyHtml,
        },
      });
      if (error) throw error;
      if (data && (data as { success?: boolean; error?: string }).success === false) {
        throw new Error((data as { error?: string }).error || "Échec de l'envoi");
      }
      toast.success("Email envoyé", {
        description: `Réponse envoyée à ${target.customerEmail}`,
      });
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      logger.error("Erreur envoi email manuel:", err);
      toast.error("Impossible d'envoyer l'email", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!sending ? onOpenChange(o) : null)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Répondre à {target.customerName}
          </DialogTitle>
          <DialogDescription>
            {target.customerEmail} — réservation du {formatDateLabel(target.date)} à{" "}
            {target.time}
          </DialogDescription>
        </DialogHeader>

        {target.notes && target.notes.trim().length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
              Note du client
            </p>
            <p className="text-sm text-amber-900 dark:text-amber-200 italic whitespace-pre-wrap">
              « {target.notes.trim()} »
            </p>
          </div>
        )}

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="manual-email-subject">Sujet</Label>
            <Input
              id="manual-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
              maxLength={SUBJECT_MAX}
              disabled={sending}
            />
            <p className="text-xs text-muted-foreground text-right">
              {subject.length}/{SUBJECT_MAX}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="manual-email-body">Message</Label>
            <Textarea
              id="manual-email-body"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
              maxLength={BODY_MAX}
              rows={12}
              disabled={sending}
              className="font-sans"
            />
            <p className="text-xs text-muted-foreground text-right">
              {body.length}/{BODY_MAX}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Annuler
          </Button>
          <Button onClick={handleSend} disabled={!canSend}>
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi…
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Envoyer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
